// Hybrid typeahead for picking a ClientCode. Free-text input; once the user
// has typed >= MIN_CHARS, a filtered dropdown of matching clients appears.
// Matching is case-insensitive on clientCode AND clientName. With `search`
// (async (needle) => [{ code, name }]) the matches come from the server
// instead of the static `options` list (debounced; stale answers are dropped).

import React, { useEffect, useRef, useState } from 'react';

const MIN_CHARS = 3;
const MAX_RESULTS = 20;

export default function ClientCodeAutocomplete({
  value,
  onChange,
  options = [],        // [{ code, name }]
  disabled,
  placeholder,
  autoFocus,
  inputClassName,
  emptyMessage = 'No matches',
  search,              // optional async (needle) => [{ code, name }]
}) {
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState({ needle: '', rows: [], busy: false });
  const blurTimer = useRef(null);
  const searchRef = useRef(search);
  searchRef.current = search;

  const needle = (value || '').toLowerCase().trim();

  useEffect(() => {
    if (!searchRef.current || needle.length < MIN_CHARS) return undefined;
    let stale = false;
    setRemote(r => ({ ...r, busy: true }));
    const t = setTimeout(async () => {
      try {
        const rows = await searchRef.current(needle);
        if (!stale) setRemote({ needle, rows: Array.isArray(rows) ? rows : [], busy: false });
      } catch {
        if (!stale) setRemote({ needle, rows: [], busy: false });
      }
    }, 250);
    return () => { stale = true; clearTimeout(t); };
  }, [needle]);

  const matches = needle.length < MIN_CHARS ? []
    : search
      ? (remote.needle === needle ? remote.rows.slice(0, MAX_RESULTS) : [])
      : options.filter(o =>
          (o.code  || '').toLowerCase().includes(needle) ||
          (o.name  || '').toLowerCase().includes(needle)
        ).slice(0, MAX_RESULTS);
  const searching = !!search && needle.length >= MIN_CHARS && remote.needle !== needle;

  function pick(code) {
    onChange(code);
    setOpen(false);
  }

  return (
    <div className="cca-wrap">
      <input
        type="text"
        className={inputClassName || 'cs-input'}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay close so option onMouseDown has a chance to fire first.
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
      />
      {open && needle.length >= MIN_CHARS && (
        <div className="cca-dropdown">
          {matches.length === 0 ? (
            <div className="cca-empty">{searching ? 'Searching…' : emptyMessage}</div>
          ) : (
            matches.map(o => (
              <div
                key={o.code}
                className={`cca-item ${o.code === value ? 'cca-item--active' : ''}`}
                onMouseDown={e => { e.preventDefault(); clearTimeout(blurTimer.current); pick(o.code); }}
              >
                <span className="cca-code">{o.code}</span>
                <span className="cca-name">{o.name}</span>
              </div>
            ))
          )}
        </div>
      )}
      {open && needle.length > 0 && needle.length < MIN_CHARS && (
        <div className="cca-dropdown">
          <div className="cca-hint">Type at least {MIN_CHARS} characters to search…</div>
        </div>
      )}
    </div>
  );
}
